<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <Sidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <div class="content">

          <v-row class="g-4 mt-4 mb-8" dense>

            <v-col cols="12" sm="12">
              <div class="column-title mb-6">
                <h5 class="m-0 text-left">Investments</h5>
                <hr class="title-separator" />
              </div>
            </v-col>

            <GlobalKyc />

            <template v-if="loading">

              <v-col cols="12" v-for="n in 4" :key="n">
                <v-card class="pa-6" rounded="lg">
                  <v-skeleton-loader type="heading" class="mb-4" />
                  <v-skeleton-loader type="text" />
                  <v-skeleton-loader type="text" width="80%" class="mt-2" />
                </v-card>
              </v-col>

            </template>

            <!-- Loop through Grouped invstment -->
            <template v-else>
              <div v-for="category in investmentsList" :key="category.id" class="investment-category mb-10">
                <!-- Header -->
                <div class="category-header">
                  <h2 class="text-h6 font-weight-bold">
                    {{ category.name }}
                  </h2>

                  <v-btn variant="text" color="success" @click="viewAll(category.uuid)">
                    View All
                    <v-icon end>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>

                <!-- Carousel -->
                <div class="carousel-wrapper">

                  <!-- Left -->
                  <v-btn v-if="category.products?.length > 3" icon class="carousel-btn left"
                    @click="scrollLeft(category.uuid)">
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>

                  <!-- Products -->
                  <div class="products-container" :ref="el => setCarouselRef(el, category.uuid)">
                    <div v-for="inv in category.products" :key="inv.uuid" class="product-slide">
                      <v-card class="product-card pa-6 h-100" flat>

                        <!-- TOP -->
                        <div class="d-flex align-center gap-2 mb-4">
                          <img :src="inv.product_image ? `${STORAGE_URL}/${inv.product_image}` : defaultImage"
                            class="product-image" />

                          <div class="text-left">
                            <div class="font-weight-bold text-body-1">
                              {{ inv.product_name }}
                            </div>
                            <div class="text-caption text-grey">
                              {{ inv.partner.name }}
                            </div>
                          </div>
                        </div>

                        <!-- INNER CARD -->
                        <v-card class="inner-mini-card pa-2 mb-3" flat>

                          <div class="d-flex justify-space-between">

                            <div>
                              <div class="text-caption text-grey">
                                Maturity Date
                              </div>

                              <div class="font-weight-medium">
                                {{ formatDate(inv.maturity_date) }}
                              </div>
                            </div>

                            <div class="text-right">

                              <div class="text-caption text-grey">
                                Rate
                              </div>

                              <div class="font-weight-medium text-green">
                                {{ formatNumber(inv.interest) }}%
                              </div>

                            </div>

                          </div>

                        </v-card>

                        <!-- Footer -->

                        <div class="d-flex justify-space-between align-center mt-auto">

                          <!-- Risk -->
                          <div class="text-caption">
                            <span :class="getRiskClass(inv.risk_category.name)">
                              {{ inv.risk_category.name }}
                            </span>
                          </div>

                          <!-- View Details -->
                          <div class="d-flex align-center gap-2">
                            <span class="text-caption font-weight-medium clickable-title"
                              @click="viewProduct(inv)">
                              View Details
                            </span>
                            <v-icon size="16" class="ms-1">mdi-arrow-right-circle</v-icon>
                          </div>


                        </div>

                      </v-card>
                    </div>
                  </div>

                  <!-- Right -->
                  <v-btn v-if="category.products?.length > 3" icon class="carousel-btn right"
                    @click="scrollRight(category.uuid)">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>

                </div>
              </div>
            </template>
          </v-row>
        </div>



      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import Sidebar from "@/components/Sidebar.vue"
import Header from "@/components/Header.vue"
import { useRouter } from "vue-router"
import { useInvStore } from "@/store/investment"
import { STORAGE_URL } from "@/api/env"
import { formatNumber, formatDate, getRiskClass } from "@/util"
import GlobalKyc from "@/components/GlobalKyc.vue"

//avatar
import avatarImage from '@/assets/crowdital-icon.png'


const router = useRouter()
const inv = useInvStore();
const carouselRefs = ref({})
// const auth = useAuthStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const loading = ref(true)
//const filters = ref({ global: { value: null, matchMode: "contains" } })

//Product type image
const defaultImage = avatarImage

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  await inv.loadInvestmentType()
  await inv.loadGroupedProducts()
  loading.value = false

})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Grouped Products */
//const investmentsList = computed(() => inv.groupedItem || [])

const investmentsList = computed(() => {
  if (!inv.groupedItem) return []

  return Array.isArray(inv.groupedItem)
    ? inv.groupedItem
    : [inv.groupedItem]
})

//View link on the card
const viewProduct = (product) => {
  router.push(`/product-detail/${product.slug}`)
}

/**Carousel */
const setCarouselRef = (el, id) => {
  if (el) {
    carouselRefs.value[id] = el
  }
}

const scrollRight = (id) => {
  carouselRefs.value[id]?.scrollBy({
    left: carouselRefs.value[id].clientWidth,
    behavior: 'smooth'
  })
}

const scrollLeft = (id) => {
  carouselRefs.value[id]?.scrollBy({
    left: -carouselRefs.value[id].clientWidth,
    behavior: 'smooth'
  })
}

//View link on the card
const viewAll = (id) => {
  router.push(`/products/${id}`)
}

</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background-color: #f8fafc;
  padding-left: 5%;
  padding-right: 5%;
}

.content {
  padding-left: 10%;
  padding-right: 10%;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 2%;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}


/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

/* Column title */
.column-title h5 {
  font-size: 1.1rem;
  font-weight: 400;
  margin-bottom: 2.5%;
}

/* Horizontal line below title */
.title-separator {
  border: none;
  height: 1px;
  background-color: #eae5e5;
  margin: 0;
  margin-bottom: 8px;
}

.inner-transaction-card>div:hover {
  background: #f9f9f9;
  border-radius: 6px;
}

.product-card {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}

.inner-mini-card {
  border: 1px solid #eae5e5;
  border-radius: 8px;
  background: #fff;
}

.small-text {
  font-size: 0.9rem;
  line-height: 1.2;
}

.clickable-title {
  cursor: pointer;
  transition: 0.2s ease;
}

.clickable-title:hover {
  opacity: 0.7;
}

.spin {
  animation: spin 1s linear infinite;
}

.investment-category {
  width: 100%;
  margin-bottom: 40px;
}

.category-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.product-slide {
  width: 33.333%;
}

.v-slide-group__content {
  gap: 16px;
}

.v-slide-group__container {
  width: 100%;
}

.v-slide-group {
  width: 100%;
}

.investment-category {
  width: 100%;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
}

.products-container {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - 32px)/3);
  gap: 16px;
  overflow-x: auto;
  scroll-behavior: smooth;
}

.products-container::-webkit-scrollbar {
  display: none;
}

.product-slide {
  width: 100%;
}

.carousel-btn {
  position: absolute;
  top: 45%;
  transform: translateY(-50%);
  z-index: 10;
  background: white !important;
  box-shadow: 0 3px 12px rgba(0, 0, 0, .15);

}

.carousel-btn.left {
  left: -20px;
}

.carousel-btn.right {
  right: -20px;
}

.products-container::-webkit-scrollbar {
  display: none;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Mobile */
@media (max-width: 600px) {
  .content {
    padding: 0;
  }

  .avatar-mobile {
    width: 32px;
    height: 32px;
  }

  .title-mobile {
    font-size: 0.9rem;
  }

  .icon-mobile {
    font-size: 16px;
  }

  .products-container {
    grid-auto-columns: 100%;
  }

  .carousel-btn {
    display: none;
  }

  @media(max-width:960px) {

    .products-container {

      grid-auto-columns: calc((100% - 16px)/2);

    }

  }
}
</style>
